import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  WxbAlert,
  WxbButton,
  WxbDataTable,
  WxbDrawer,
  WxbEmpty,
  WxbInput,
  WxbModal,
  WxbPageHeader,
  WxbPageSection,
  WxbPageShell,
  WxbPopconfirm,
  WxbSearchInput,
  WxbSegmented,
  WxbSelect,
  WxbSpinner,
  WxbStatusBadge,
  WxbTag,
  WxbValidationList,
  wxbToast,
} from '../components/wxb-ui';
import EquipmentClassModal from '../components/ProdEquipmentStateCatalog/EquipmentClassModal';
import EquipmentStateModelDrawer, {
  EquipmentStateModelEditorView,
} from '../components/ProdEquipmentStateCatalog/EquipmentStateModelDrawer';
import ResourceBindingPanel from '../components/ProdEquipmentStateCatalog/ResourceBindingPanel';
import {
  ACTION_ORIGIN_LABELS,
  ATTRIBUTE_KIND_LABELS,
  FABRICATION_TYPE_LABELS,
  formatCondition,
  formatEffect,
  formatTemporalExpression,
  isWritableCatalogVersion,
  RESOURCE_KIND_LABELS,
  VERSION_STATUS_LABELS,
} from '../components/ProdEquipmentStateCatalog/equipmentStateCatalogModel';
import { useAuth } from '../contexts/AuthContext';
import {
  normalizeEquipmentStateCatalogError,
  prodEquipmentStateCatalogApi,
} from '../services/prodEquipmentStateCatalogApi';
import { prodResourceTopologyApi } from '../services/prodResourceTopologyApi';
import type {
  EquipmentStateBinding,
  EquipmentStateCatalogConflict,
  EquipmentStateCatalogVersion,
  EquipmentStateCatalogWorkspace,
  EquipmentStateClass,
  EquipmentStateClassDraft,
  EquipmentStateClassModel,
} from '../types/prodEquipmentStateCatalog';
import type { ResourceTopologyVersion } from '../types/prodResourceTopology';
import './EquipmentStateMachinePage.css';

type WorkspaceView = 'models' | 'bindings' | 'validation';

const parseFacilityCodes = (raw: string | undefined): string[] => {
  const values = (raw ?? 'F1').split(',').map((value) => value.trim()).filter(Boolean);
  return values.length > 0 ? Array.from(new Set(values)) : ['F1'];
};

const FACILITY_CODES = parseFacilityCodes(process.env.REACT_APP_PROD_RESOURCE_FACILITY_CODES);

const versionTone = (status: EquipmentStateCatalogVersion['status']) => {
  if (status === 'PUBLISHED') return 'success' as const;
  if (status === 'VALIDATED') return 'info' as const;
  if (status === 'DRAFT') return 'warning' as const;
  return 'neutral' as const;
};

const formatTime = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { hour12: false });
};

const EquipmentStateMachinePage: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const canWrite = !user || hasPermission('MASTER_RESOURCE_WRITE');
  const canOperate = !user || hasPermission('MASTER_RESOURCE_OPERATE');
  const [facilityCode, setFacilityCode] = useState(FACILITY_CODES[0]);
  const [versions, setVersions] = useState<EquipmentStateCatalogVersion[]>([]);
  const [topologyVersions, setTopologyVersions] = useState<ResourceTopologyVersion[]>([]);
  const [workspace, setWorkspace] = useState<EquipmentStateCatalogWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<EquipmentStateCatalogConflict | null>(null);
  const [view, setView] = useState<WorkspaceView>('models');
  const [classSearch, setClassSearch] = useState('');
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [classModalOpen, setClassModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<EquipmentStateClass | null>(null);
  const [modelDrawerOpen, setModelDrawerOpen] = useState(false);
  const [modelDrawerView, setModelDrawerView] = useState<EquipmentStateModelEditorView>('hygiene');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftTopologyId, setDraftTopologyId] = useState<string | undefined>();
  const [draftBaseVersionId, setDraftBaseVersionId] = useState<string | undefined>();
  const [publishOpen, setPublishOpen] = useState(false);
  const [rebaseOpen, setRebaseOpen] = useState(false);
  const [rebaseTopologyId, setRebaseTopologyId] = useState<string | undefined>();
  const [bindingDirty, setBindingDirty] = useState(false);

  const loadWorkspace = useCallback(async (versionId: string) => {
    const next = await prodEquipmentStateCatalogApi.getWorkspace(versionId);
    setWorkspace(next);
    setSelectedClassId((current) => (
      current && next.classes.some((equipmentClass) => equipmentClass.classId === current)
        ? current
        : next.classes.find((equipmentClass) => equipmentClass.lifecycleStatus === 'ACTIVE')?.classId
          ?? next.classes[0]?.classId
          ?? null
    ));
    return next;
  }, []);

  const loadFacility = useCallback(async (preferredVersionId?: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const [catalogVersions, resourceVersions] = await Promise.all([
        prodEquipmentStateCatalogApi.listVersions(facilityCode),
        prodResourceTopologyApi.listVersions(facilityCode),
      ]);
      setVersions(catalogVersions);
      setTopologyVersions(resourceVersions);
      const preferred = preferredVersionId
        ? catalogVersions.find((version) => version.id === preferredVersionId)
        : null;
      const selected = preferred
        ?? catalogVersions.find((version) => version.status === 'DRAFT' || version.status === 'VALIDATED')
        ?? catalogVersions.find((version) => version.status === 'PUBLISHED')
        ?? catalogVersions[0]
        ?? null;
      if (selected) await loadWorkspace(selected.id);
      else {
        setWorkspace(null);
        setSelectedClassId(null);
      }
    } catch (error) {
      setLoadError(normalizeEquipmentStateCatalogError(error).message);
    } finally {
      setLoading(false);
    }
  }, [facilityCode, loadWorkspace]);

  useEffect(() => {
    loadFacility();
  }, [loadFacility]);

  const handleFailure = useCallback((error: unknown) => {
    const normalized = normalizeEquipmentStateCatalogError(error);
    setRequestError(normalized.message);
    if (normalized.kind === 'revision-conflict') {
      setConflict(normalized);
    } else if (normalized.issues.length > 0) {
      setWorkspace((current) => current ? { ...current, issues: normalized.issues } : current);
      setView('validation');
    }
    return normalized;
  }, []);

  const updateVersion = useCallback((version: EquipmentStateCatalogVersion) => {
    setVersions((current) => current.map((item) => item.id === version.id ? version : item));
    setWorkspace((current) => current && current.version.id === version.id
      ? { ...current, version }
      : current);
  }, []);

  const chooseVersion = async (versionId: string) => {
    setLoading(true);
    setLoadError(null);
    setConflict(null);
    setRequestError(null);
    try {
      await loadWorkspace(versionId);
      setHistoryOpen(false);
      setView('models');
    } catch (error) {
      setLoadError(normalizeEquipmentStateCatalogError(error).message);
    } finally {
      setLoading(false);
    }
  };

  const openDraftModal = () => {
    const candidateTopology = topologyVersions.find((version) => version.status === 'PUBLISHED')
      ?? topologyVersions.find((version) => version.status === 'DRAFT' || version.status === 'VALIDATED');
    setDraftTopologyId(candidateTopology?.id);
    setDraftBaseVersionId(
      workspace && (workspace.version.status === 'PUBLISHED' || workspace.version.status === 'SUPERSEDED')
        ? workspace.version.id
        : undefined,
    );
    setDraftName('');
    setDraftOpen(true);
  };

  const createDraft = async () => {
    if (!draftTopologyId) return;
    setSaving(true);
    setRequestError(null);
    try {
      const version = await prodEquipmentStateCatalogApi.createDraft({
        facilityCode,
        name: draftName.trim() || undefined,
        resourceTopologyVersionId: draftTopologyId,
        baseVersionId: draftBaseVersionId || undefined,
      });
      setDraftOpen(false);
      await loadFacility(version.id);
      wxbToast.success('设备状态目录草稿已创建');
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const saveClass = async (draft: EquipmentStateClassDraft) => {
    if (!workspace) return;
    setSaving(true);
    setRequestError(null);
    try {
      const result = editingClass
        ? await prodEquipmentStateCatalogApi.updateClass(
          workspace.version.id,
          editingClass.classId,
          workspace.version.revision,
          {
            name: draft.name,
            fabricationType: draft.fabricationType,
            applicableResourceKinds: draft.applicableResourceKinds,
            note: draft.note,
          },
        )
        : await prodEquipmentStateCatalogApi.createClass(
          workspace.version.id,
          workspace.version.revision,
          draft,
        );
      const next = await loadWorkspace(workspace.version.id);
      updateVersion(next.version);
      setSelectedClassId(result.equipmentClass?.classId
        ?? next.classes.find((item) => item.code === draft.code)?.classId
        ?? selectedClassId);
      setClassModalOpen(false);
      wxbToast.success(editingClass ? '设备类别已更新' : '设备类别已创建');
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const retireClass = async (equipmentClass: EquipmentStateClass) => {
    if (!workspace) return;
    setSaving(true);
    try {
      await prodEquipmentStateCatalogApi.retireClass(
        workspace.version.id,
        equipmentClass.classId,
        workspace.version.revision,
      );
      const next = await loadWorkspace(workspace.version.id);
      updateVersion(next.version);
      wxbToast.success('设备类别已退役');
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const saveModel = async (model: EquipmentStateClassModel): Promise<boolean> => {
    if (!workspace || !selectedClassId) return false;
    setSaving(true);
    setRequestError(null);
    try {
      await prodEquipmentStateCatalogApi.replaceClassModel(
        workspace.version.id,
        selectedClassId,
        workspace.version.revision,
        model,
      );
      const next = await loadWorkspace(workspace.version.id);
      updateVersion(next.version);
      wxbToast.success('状态与周期定义、动作和就绪条件已保存');
      return true;
    } catch (error) {
      handleFailure(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveBindings = async (bindings: EquipmentStateBinding[]): Promise<boolean> => {
    if (!workspace) return false;
    setSaving(true);
    setRequestError(null);
    try {
      await prodEquipmentStateCatalogApi.replaceBindings(
        workspace.version.id,
        workspace.version.revision,
        bindings,
      );
      const next = await loadWorkspace(workspace.version.id);
      updateVersion(next.version);
      wxbToast.success('资源类别绑定已保存');
      return true;
    } catch (error) {
      handleFailure(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const validateCatalog = async () => {
    if (!workspace) return;
    setValidating(true);
    setRequestError(null);
    try {
      const result = await prodEquipmentStateCatalogApi.validate(
        workspace.version.id,
        workspace.version.revision,
      );
      setWorkspace((current) => current ? { ...current, version: result.version, issues: result.issues } : current);
      updateVersion(result.version);
      setView('validation');
      const hardCount = result.issues.filter((issue) => issue.hardOrSoft === 'hard').length;
      if (hardCount === 0) wxbToast.success('设备类别与状态机已通过硬约束校验');
      else wxbToast.warning(`校验发现 ${hardCount} 个硬错误`);
    } catch (error) {
      handleFailure(error);
    } finally {
      setValidating(false);
    }
  };

  const publishCatalog = async () => {
    if (!workspace) return;
    setPublishing(true);
    try {
      const version = await prodEquipmentStateCatalogApi.publish(
        workspace.version.id,
        workspace.version.revision,
      );
      setPublishOpen(false);
      await loadFacility(version.id);
      wxbToast.success(`设备状态目录 V${version.versionNo} 已发布`);
    } catch (error) {
      handleFailure(error);
    } finally {
      setPublishing(false);
    }
  };

  const abandonCatalog = async () => {
    if (!workspace) return;
    setSaving(true);
    try {
      const version = await prodEquipmentStateCatalogApi.abandon(
        workspace.version.id,
        workspace.version.revision,
      );
      await loadFacility(version.id);
      wxbToast.success('设备状态目录草稿已放弃');
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const rebaseTopology = async () => {
    if (!workspace || !rebaseTopologyId) return;
    setSaving(true);
    setRequestError(null);
    try {
      const result = await prodEquipmentStateCatalogApi.rebaseTopology(
        workspace.version.id,
        workspace.version.revision,
        rebaseTopologyId,
      );
      const next = await loadWorkspace(workspace.version.id);
      setWorkspace({ ...next, issues: result.issues });
      updateVersion(result.version);
      setRebaseOpen(false);
      if (result.issues.length > 0) setView('validation');
      wxbToast.success(`拓扑基线已同步，保留 ${result.preservedBindingCount} 条稳定身份绑定`);
    } catch (error) {
      handleFailure(error);
    } finally {
      setSaving(false);
    }
  };

  const selectedClass = workspace?.classes.find((item) => item.classId === selectedClassId) ?? null;
  const writable = isWritableCatalogVersion(workspace?.version);
  const activeDraft = versions.find((version) => version.status === 'DRAFT' || version.status === 'VALIDATED');
  const currentTopology = workspace
    ? topologyVersions.find((version) => version.id === workspace.version.resourceTopologyVersionId)
    : null;
  const baselineDrift = Boolean(workspace && currentTopology
    && currentTopology.revision !== workspace.version.resourceTopologyRevision);
  const baselineUnpublished = Boolean(workspace && workspace.resourceTopology.status !== 'PUBLISHED');
  const hardIssueCount = workspace?.issues.filter((issue) => issue.hardOrSoft === 'hard').length ?? 0;
  const softIssueCount = workspace?.issues.filter((issue) => issue.hardOrSoft === 'soft').length ?? 0;
  const publishBlockingSoftCount = workspace?.issues.filter((issue) => issue.constraintCode === 'STATE_BINDING_REQUIRED').length ?? 0;
  const publishAllowed = Boolean(workspace
    && workspace.version.status === 'VALIDATED'
    && workspace.version.validatedRevision === workspace.version.revision
    && hardIssueCount === 0
    && publishBlockingSoftCount === 0
    && !baselineDrift
    && !baselineUnpublished);

  const filteredClasses = useMemo(() => {
    const classes = workspace?.classes ?? [];
    const query = classSearch.trim().toLowerCase();
    if (!query) return classes;
    return classes.filter((equipmentClass) => (
      equipmentClass.code.toLowerCase().includes(query)
      || equipmentClass.name.toLowerCase().includes(query)
    ));
  }, [classSearch, workspace?.classes]);

  const historyColumns: ColumnsType<EquipmentStateCatalogVersion> = [
    { title: '版本', dataIndex: 'versionNo', width: 80, render: (value: number) => `V${value}` },
    { title: '名称', dataIndex: 'name' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (status: EquipmentStateCatalogVersion['status']) => (
        <WxbStatusBadge tone={versionTone(status)} label={VERSION_STATUS_LABELS[status]} size="sm" />
      ),
    },
    { title: 'revision', dataIndex: 'revision', width: 90 },
    { title: '拓扑 revision', dataIndex: 'resourceTopologyRevision', width: 120 },
    { title: '发布时间', dataIndex: 'publishedAt', width: 170, render: formatTime },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_, version) => (
        <WxbButton type="button" size="sm" variant="ghost" onClick={() => chooseVersion(version.id)}>
          查看
        </WxbButton>
      ),
    },
  ];

  const validationItems = (workspace?.issues ?? []).map((issue) => ({
    key: issue.id,
    level: issue.hardOrSoft === 'hard' ? 'error' as const
      : issue.severity === 'warning' ? 'warning' as const : 'info' as const,
    title: issue.violationReason,
    description: issue.suggestedAction ?? issue.violationMessageTemplate ?? undefined,
    source: `${issue.constraintCode} · ${issue.hardOrSoft === 'hard' ? '硬约束' : '软提示'}`,
  }));

  return (
    <WxbPageShell size="full" gap="sm" className="equipment-state-catalog-page">
      <WxbPageHeader
        eyebrow="生产排产数据底座 · 模块 2"
        title="设备类别与状态机"
        description="统一维护设备类别的状态与周期定义、稳定动作和就绪条件；周期状态属于离散状态机。"
        meta={workspace ? (
          <>
            <WxbStatusBadge
              tone={versionTone(workspace.version.status)}
              label={VERSION_STATUS_LABELS[workspace.version.status]}
              size="sm"
            />
            <WxbTag color="neutral">revision {workspace.version.revision}</WxbTag>
          </>
        ) : undefined}
        actions={(
          <div className="esc-header-actions">
            <WxbButton
              type="button"
              variant="ghost"
              disabled={bindingDirty}
              title={bindingDirty ? '请先保存或放弃资源绑定修改' : undefined}
              onClick={() => setHistoryOpen(true)}
            >
              版本历史
            </WxbButton>
            {workspace && writable ? (
              <>
                <WxbPopconfirm
                  title={`放弃草稿 V${workspace.version.versionNo}？`}
                  description="放弃后版本只读，当前定义不会发布。"
                  okText="确认放弃"
                  disabled={!canOperate || saving || bindingDirty}
                  onConfirm={abandonCatalog}
                >
                  <WxbButton type="button" variant="danger" disabled={!canOperate || saving || bindingDirty}>
                    放弃草稿
                  </WxbButton>
                </WxbPopconfirm>
                <WxbButton
                  type="button"
                  variant="secondary"
                  disabled={!canOperate || validating || saving || bindingDirty}
                  onClick={validateCatalog}
                >
                  {validating ? '校验中...' : '校验草稿'}
                </WxbButton>
                <WxbButton
                  type="button"
                  disabled={!canOperate || !publishAllowed || publishing || bindingDirty}
                  title={!publishAllowed ? '当前 revision、拓扑基线、硬约束和必需资源绑定必须全部通过校验' : undefined}
                  onClick={() => setPublishOpen(true)}
                >
                  发布
                </WxbButton>
              </>
            ) : activeDraft && workspace?.version.id !== activeDraft.id ? (
              <WxbButton type="button" onClick={() => chooseVersion(activeDraft.id)}>
                返回当前草稿
              </WxbButton>
            ) : (
              <WxbButton type="button" disabled={!canWrite || topologyVersions.length === 0} onClick={openDraftModal}>
                新建草稿
              </WxbButton>
            )}
          </div>
        )}
      />

      <div className="esc-context-bar">
        <WxbSelect
          label="设施"
          value={facilityCode}
          disabled={bindingDirty}
          options={FACILITY_CODES.map((code) => ({ value: code, label: code }))}
          onChange={(value) => setFacilityCode(String(value))}
        />
        <WxbSelect
          label="当前版本"
          value={workspace?.version.id}
          options={versions.map((version) => ({
            value: version.id,
            label: `V${version.versionNo} · ${version.name} · ${VERSION_STATUS_LABELS[version.status]}`,
          }))}
          disabled={loading || versions.length === 0 || bindingDirty}
          onChange={(value) => chooseVersion(String(value))}
        />
        {workspace && (
          <div className="esc-baseline-summary">
            <span>资源拓扑基线</span>
            <strong>V{workspace.resourceTopology.versionNo} · {workspace.resourceTopology.name}</strong>
            <WxbStatusBadge
              tone={workspace.resourceTopology.status === 'PUBLISHED' ? 'success' : 'warning'}
              label={VERSION_STATUS_LABELS[workspace.resourceTopology.status]}
              size="sm"
            />
            <code>revision {workspace.version.resourceTopologyRevision}</code>
            {writable && (
              <WxbButton
                type="button"
                size="sm"
                variant="ghost"
                disabled={!canOperate || saving || bindingDirty || !topologyVersions.some((version) => (
                  ['DRAFT', 'VALIDATED', 'PUBLISHED'].includes(version.status)
                ))}
                onClick={() => {
                  const currentAllowed = topologyVersions.find((version) => (
                    version.id === workspace.version.resourceTopologyVersionId
                    && ['DRAFT', 'VALIDATED', 'PUBLISHED'].includes(version.status)
                  ));
                  const fallback = topologyVersions.find((version) => version.status === 'PUBLISHED')
                    ?? topologyVersions.find((version) => version.status === 'DRAFT' || version.status === 'VALIDATED');
                  setRebaseTopologyId(currentAllowed?.id ?? fallback?.id);
                  setRebaseOpen(true);
                }}
              >
                同步拓扑基线
              </WxbButton>
            )}
          </div>
        )}
      </div>

      {requestError && (
        <WxbAlert variant="error" title="操作未完成">
          <div className="esc-alert-actions">
            <span>{requestError}</span>
            <WxbButton type="button" size="sm" variant="ghost" onClick={() => setRequestError(null)}>
              关闭提示
            </WxbButton>
          </div>
        </WxbAlert>
      )}
      {conflict && workspace && (
        <WxbAlert variant="error" title="目录已被其他用户更新">
          <div className="esc-alert-actions">
            <span>
              当前页面 revision {conflict.expectedRevision ?? workspace.version.revision}，服务端 revision {conflict.actualRevision ?? '已变化'}。
            </span>
            <WxbButton
              type="button"
              size="sm"
              variant="secondary"
              onClick={async () => {
                await loadWorkspace(workspace.version.id);
                setConflict(null);
                setRequestError(null);
              }}
            >
              重新加载
            </WxbButton>
          </div>
        </WxbAlert>
      )}
      {baselineDrift && (
        <WxbAlert variant="error" title="资源拓扑基线已漂移">
          目录固定在 revision {workspace?.version.resourceTopologyRevision}，当前拓扑已是 revision {currentTopology?.revision}。
          请使用“同步拓扑基线”，并处理同步后返回的缺失或不兼容绑定诊断。
        </WxbAlert>
      )}
      {baselineUnpublished && (
        <WxbAlert title="资源拓扑基线尚未发布">
          草稿可以继续维护，但设备状态目录发布前必须先发布该资源拓扑版本，再重新校验本目录。
        </WxbAlert>
      )}

      {loading ? (
        <div className="esc-loading"><WxbSpinner tip="加载设备类别与状态机..." /></div>
      ) : loadError ? (
        <WxbAlert variant="error" title="设备状态目录加载失败">
          <div className="esc-alert-actions">
            <span>{loadError}</span>
            <WxbButton type="button" variant="secondary" onClick={() => loadFacility(workspace?.version.id)}>
              重试
            </WxbButton>
          </div>
        </WxbAlert>
      ) : !workspace ? (
        <WxbEmpty
          description={topologyVersions.length === 0
            ? '当前设施还没有资源拓扑版本，请先完成模块 1。'
            : '当前设施还没有设备状态目录版本。'}
          action={canWrite && topologyVersions.length > 0 ? (
            <WxbButton type="button" onClick={openDraftModal}>创建第一个草稿</WxbButton>
          ) : undefined}
        />
      ) : (
        <>
          <div className="esc-workspace-nav">
            <WxbSegmented
              value={view}
              options={[
                { value: 'models', label: `类别与模型 (${workspace.classes.length})` },
                { value: 'bindings', label: `资源绑定 (${workspace.bindings.length})` },
                { value: 'validation', label: `校验问题 (${workspace.issues.length})` },
              ]}
              onChange={(value) => {
                const next = value as WorkspaceView;
                if (bindingDirty && next !== 'bindings') {
                  wxbToast.warning('请先保存资源绑定');
                  return;
                }
                setView(next);
              }}
            />
            {!writable && <span>当前版本只读；从历史版本新建草稿后才能修改。</span>}
          </div>

          {view === 'models' && (
            <main className="esc-model-workspace">
              <WxbPageSection
                variant="framed"
                density="compact"
                className="esc-class-pane"
                title="设备类别"
                description={`${workspace.classes.filter((item) => item.lifecycleStatus === 'ACTIVE').length} 个启用类别`}
                actions={(
                  <WxbButton
                    type="button"
                    size="sm"
                    disabled={!writable || !canWrite}
                    onClick={() => {
                      setEditingClass(null);
                      setClassModalOpen(true);
                    }}
                  >
                    新建类别
                  </WxbButton>
                )}
              >
                <WxbSearchInput
                  value={classSearch}
                  onChange={setClassSearch}
                  placeholder="搜索类别编码或名称"
                  aria-label="搜索设备类别"
                />
                <div className="esc-class-list">
                  {filteredClasses.map((equipmentClass) => (
                    <WxbButton
                      type="button"
                      variant="ghost"
                      className={`esc-class-item${equipmentClass.classId === selectedClassId ? ' is-selected' : ''}`}
                      key={equipmentClass.classId}
                      onClick={() => setSelectedClassId(equipmentClass.classId)}
                    >
                      <span className="esc-class-item-head">
                        <strong>{equipmentClass.name}</strong>
                        {equipmentClass.lifecycleStatus === 'RETIRED' && <WxbTag color="neutral">已退役</WxbTag>}
                      </span>
                      <code>{equipmentClass.code}</code>
                      <span className="esc-class-item-meta">
                        {FABRICATION_TYPE_LABELS[equipmentClass.fabricationType]} · {equipmentClass.model.attributes.length} 属性 · {equipmentClass.model.actions.length} 动作
                      </span>
                    </WxbButton>
                  ))}
                  {filteredClasses.length === 0 && (
                    <WxbEmpty description={classSearch ? '没有符合搜索条件的类别。' : '当前版本还没有设备类别。'} />
                  )}
                </div>
              </WxbPageSection>

              <WxbPageSection
                variant="framed"
                density="compact"
                className="esc-class-detail"
                title={selectedClass?.name ?? '类别状态模型'}
                description={selectedClass
                  ? `状态与周期定义、动作和就绪条件统一属于该设备类别模型。${selectedClass.note ? ` ${selectedClass.note}` : ''}`
                  : '选择左侧设备类别查看或维护统一的类别状态模型。'}
                actions={selectedClass ? (
                  <div className="esc-section-actions">
                    <WxbButton
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!writable || !canWrite || selectedClass.lifecycleStatus === 'RETIRED'}
                      onClick={() => {
                        setEditingClass(selectedClass);
                        setClassModalOpen(true);
                      }}
                    >
                      编辑类别
                    </WxbButton>
                    <WxbButton
                      type="button"
                      size="sm"
                      onClick={() => {
                        setModelDrawerOpen(true);
                      }}
                    >
                      {writable && canWrite && selectedClass.lifecycleStatus === 'ACTIVE' ? '维护状态模型' : '查看状态模型'}
                    </WxbButton>
                    {writable && selectedClass.lifecycleStatus === 'ACTIVE' && (
                      <WxbPopconfirm
                        title={`退役类别“${selectedClass.name}”？`}
                        description={workspace.bindings.some((item) => item.classId === selectedClass.classId)
                          ? '该类别仍有资源绑定。请先在“资源绑定”中解除或改绑。'
                          : '退役后该类别不能再绑定新资源。'}
                        okText="确认退役"
                        disabled={!canWrite || saving || workspace.bindings.some((item) => item.classId === selectedClass.classId)}
                        onConfirm={() => retireClass(selectedClass)}
                      >
                        <WxbButton
                          type="button"
                          size="sm"
                          variant="danger"
                          disabled={!canWrite || saving || workspace.bindings.some((item) => item.classId === selectedClass.classId)}
                          title={workspace.bindings.some((item) => item.classId === selectedClass.classId)
                            ? '请先解除或修改该类别的资源绑定'
                            : undefined}
                        >
                          退役
                        </WxbButton>
                      </WxbPopconfirm>
                    )}
                  </div>
                ) : undefined}
              >
                {selectedClass ? (
                  <div className="esc-class-overview">
                    <div className="esc-class-tags">
                      <WxbTag color="cyan">{FABRICATION_TYPE_LABELS[selectedClass.fabricationType]}</WxbTag>
                      {selectedClass.applicableResourceKinds.map((kind) => (
                        <WxbTag color="neutral" key={kind}>{RESOURCE_KIND_LABELS[kind]}</WxbTag>
                      ))}
                    </div>
                    <div className="esc-model-summary-grid">
                      <div><strong>{selectedClass.model.attributes.length}</strong><span>状态与周期定义</span></div>
                      <div><strong>{selectedClass.model.actions.length}</strong><span>稳定动作</span></div>
                      <div><strong>{selectedClass.model.readyConditions.length}</strong><span>就绪条件（AND）</span></div>
                      <div><strong>{workspace.bindings.filter((item) => item.classId === selectedClass.classId).length}</strong><span>资源绑定</span></div>
                    </div>
                    <div className="esc-overview-sections">
                      <section>
                        <h3>状态与周期定义</h3>
                        {selectedClass.model.attributes.length > 0 ? selectedClass.model.attributes.map((attribute) => (
                          <div className="esc-overview-row" key={attribute.code}>
                            <span>
                              <strong>{attribute.name}</strong>
                              <code>{attribute.code}</code>
                            </span>
                            <WxbTag color={attribute.temporalTransition ? 'amber' : attribute.kind === 'DISCRETE' ? 'blue' : attribute.kind === 'COUNTER' ? 'green' : 'amber'}>
                              {attribute.temporalTransition ? '周期状态（离散）' : ATTRIBUTE_KIND_LABELS[attribute.kind]}
                            </WxbTag>
                            <span>
                              {attribute.temporalTransition
                                ? `有效 ${attribute.temporalTransition.validValueCode} → 到期 ${attribute.temporalTransition.expiredValueCode} · ${formatTemporalExpression(attribute.temporalTransition.expiryRule.expression)}`
                                : attribute.kind === 'DISCRETE'
                                ? attribute.values.map((value) => value.code).join('、')
                                : attribute.kind === 'COUNTER'
                                  ? `初始 ${attribute.initialNumber} ${attribute.unit ?? ''}`
                                  : `${attribute.clockKind} · ${formatTemporalExpression(attribute.temporalExpression)}`}
                            </span>
                          </div>
                        )) : <div className="esc-inline-empty">尚未定义属性。</div>}
                      </section>
                      <section>
                        <h3>动作守卫与效果</h3>
                        {selectedClass.model.actions.length > 0 ? selectedClass.model.actions.map((action) => (
                          <div className="esc-overview-row esc-action-overview-row" key={action.actionCode}>
                            <span>
                              <strong>{action.name}</strong>
                              <code>{action.actionCode}</code>
                            </span>
                            <WxbTag color={action.origin === 'DERIVABLE' ? 'green' : 'blue'}>
                              {ACTION_ORIGIN_LABELS[action.origin]}
                            </WxbTag>
                            <span>{action.guards.map(formatCondition).join(' ∧ ') || '无守卫'}</span>
                            <span>{action.effects.map(formatEffect).join('；') || '无效果'}</span>
                          </div>
                        )) : <div className="esc-inline-empty">尚未定义动作。</div>}
                      </section>
                      <section>
                        <h3>类别就绪条件</h3>
                        <div className="esc-ready-expression">
                          <span>ready :=</span>
                          <code>{selectedClass.model.readyConditions.map(formatCondition).join(' ∧ ') || '未定义'}</code>
                        </div>
                      </section>
                    </div>
                  </div>
                ) : (
                  <WxbEmpty description="选择一个设备类别查看状态模型。" />
                )}
              </WxbPageSection>
            </main>
          )}

          {view === 'bindings' && (
            <WxbPageSection
              variant="framed"
              density="compact"
              title="资源与设备类别绑定"
              description="绑定基于模块 1 的稳定资源身份；同步拓扑时不静默删除失效绑定。"
            >
              <ResourceBindingPanel
                resources={workspace.topologyResources}
                classes={workspace.classes}
                bindings={workspace.bindings}
                readOnly={!writable || !canWrite}
                saving={saving}
                onSave={saveBindings}
                onDirtyChange={setBindingDirty}
              />
            </WxbPageSection>
          )}

          {view === 'validation' && (
            <WxbPageSection
              variant="framed"
              density="compact"
              title="目录校验"
              description={`${hardIssueCount} 个硬错误，${softIssueCount} 个软提示。校验结果只对 revision ${workspace.version.validatedRevision ?? '—'} 有效。`}
              actions={writable ? (
                <WxbButton
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={!canOperate || validating}
                  onClick={validateCatalog}
                >
                  {validating ? '校验中...' : '重新校验'}
                </WxbButton>
              ) : undefined}
            >
              <WxbValidationList
                density="compact"
                items={validationItems}
                emptyText={workspace.version.validatedRevision === workspace.version.revision
                  ? '当前 revision 未发现校验问题。'
                  : '当前 revision 尚未校验。'}
              />
            </WxbPageSection>
          )}
        </>
      )}

      <EquipmentClassModal
        open={classModalOpen}
        initialClass={editingClass}
        saving={saving}
        onCancel={() => setClassModalOpen(false)}
        onSave={saveClass}
      />
      <EquipmentStateModelDrawer
        open={modelDrawerOpen}
        equipmentClass={selectedClass}
        readOnly={!writable || !canWrite || selectedClass?.lifecycleStatus === 'RETIRED'}
        saving={saving}
        activeView={modelDrawerView}
        onActiveViewChange={setModelDrawerView}
        onClose={() => setModelDrawerOpen(false)}
        onSave={saveModel}
      />

      <WxbDrawer open={historyOpen} title="设备状态目录版本历史" width={820} onClose={() => setHistoryOpen(false)}>
        <WxbDataTable<EquipmentStateCatalogVersion>
          rowKey="id"
          columns={historyColumns}
          dataSource={versions}
          pagination={false}
          density="compact"
          emptyState={{ description: '当前设施还没有设备状态目录版本。' }}
        />
      </WxbDrawer>

      <WxbModal
        open={draftOpen}
        title="新建设备状态目录草稿"
        okText="创建草稿"
        confirmLoading={saving}
        onCancel={() => setDraftOpen(false)}
        onOk={createDraft}
        width={640}
      >
        <div className="esc-modal-stack">
          <WxbInput
            label="版本名称"
            value={draftName}
            placeholder="可留空使用默认名称"
            onChange={(event) => setDraftName(event.target.value)}
          />
          <WxbSelect
            label="资源拓扑基线"
            value={draftTopologyId}
            options={topologyVersions
              .filter((version) => ['DRAFT', 'VALIDATED', 'PUBLISHED'].includes(version.status))
              .map((version) => ({
                value: version.id,
                label: `V${version.versionNo} · ${version.name} · ${VERSION_STATUS_LABELS[version.status]} · revision ${version.revision}`,
              }))}
            placeholder="选择模块 1 的资源拓扑版本"
            onChange={(value) => setDraftTopologyId(String(value))}
          />
          <WxbSelect
            label="复制目录版本（可选）"
            allowClear
            value={draftBaseVersionId}
            options={versions
              .filter((version) => version.status === 'PUBLISHED' || version.status === 'SUPERSEDED')
              .map((version) => ({ value: version.id, label: `V${version.versionNo} · ${version.name}` }))}
            placeholder="不复制，创建空草稿"
            onChange={(value) => setDraftBaseVersionId(value ? String(value) : undefined)}
          />
          {draftTopologyId && topologyVersions.find((version) => version.id === draftTopologyId)?.status !== 'PUBLISHED' && (
            <WxbAlert title="选择了未发布拓扑">
              可以并行维护草稿，但设备状态目录发布前必须先发布该拓扑并重新校验。
            </WxbAlert>
          )}
        </div>
      </WxbModal>

      <WxbModal
        open={rebaseOpen}
        title="同步资源拓扑基线"
        okText="同步并检查绑定"
        confirmLoading={saving}
        onCancel={() => setRebaseOpen(false)}
        onOk={rebaseTopology}
        width={620}
      >
        <div className="esc-modal-stack">
          <p>稳定资源身份绑定会原样保留；目标拓扑缺失资源或类别不兼容时，服务端会立即返回硬诊断，不会静默删除。</p>
          <WxbSelect
            label="目标拓扑版本"
            value={rebaseTopologyId}
            options={topologyVersions
              .filter((version) => ['DRAFT', 'VALIDATED', 'PUBLISHED'].includes(version.status))
              .map((version) => ({
                value: version.id,
                label: `V${version.versionNo} · ${version.name} · ${VERSION_STATUS_LABELS[version.status]} · revision ${version.revision}`,
              }))}
            onChange={(value) => setRebaseTopologyId(String(value))}
          />
        </div>
      </WxbModal>

      <WxbModal
        open={publishOpen}
        title={`发布设备状态目录 V${workspace?.version.versionNo ?? ''}`}
        okText="确认发布"
        confirmLoading={publishing}
        onCancel={() => setPublishOpen(false)}
        onOk={publishCatalog}
      >
        发布后当前版本不可编辑，并将取代该设施此前的已发布设备状态目录。确认基线拓扑和所有资源绑定均已复核。
      </WxbModal>
    </WxbPageShell>
  );
};

export default EquipmentStateMachinePage;
